import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useLicense } from "../../context/licenseContext";
import { useToast } from "../../context/toastContext";
import { useCoaJobs } from "../../context/coaJobsContext";
import apiClient from "../../api/apiClient";

export const COA_JOB_KEY = "coaProcessingJob";

const COAUpload = ({ userId, setCOAProcessing, fetchCoas, className, coaId }) => {
    const { currentLicense } = useLicense();
    const toast = useToast();
    const { submitJobs } = useCoaJobs();
    const [isHovered, setIsHovered] = useState(false);

    const handleMouseEnter = () => setIsHovered(true);
    const handleMouseLeave = () => setIsHovered(false);

    const baseStyle = className ? undefined : {
        borderRadius: "var(--radius-md)",
        border: "1px solid var(--color-border)",
        display: "flex", 
        justifyContent: "center",
        alignItems: "center",
        width: "auto",
        minHeight: "36px",
        cursor: "pointer",
        transition: "background-color var(--transition-fast)",
        boxSizing: "border-box",
        padding: "0 var(--space-3)",
    };

    const hoverStyle = className ? undefined : {
        backgroundColor: "var(--color-accent-muted)",
        borderColor: "var(--color-accent)",
    };

    const processFiles = async (acceptedFiles) => {
        try {
            const allowedTypes = ["application/pdf", "application/x-pdf", "image/jpeg", "image/png", "image/webp"];
            if (acceptedFiles.some((file) => !allowedTypes.includes(file.type))) {
                toast.warning("Only PDF and image files (JPEG, PNG, WebP) are accepted.");
                return;
            }

            const formData = new FormData();
            let response;

            setCOAProcessing(true);

            if (coaId) {
                if (acceptedFiles.length > 1) {
                    toast.warning("Only one file can be uploaded at a time when adding to an existing COA.");
                    setCOAProcessing(false);
                    return;
                }
                formData.append("file", acceptedFiles[0]);
                response = await apiClient.post(`/processCOA/uploadPanel`, formData, {
                    headers: {
                        company: currentLicense.company,
                        state: currentLicense.state,
                        industry: currentLicense.industry,
                        profile: currentLicense.profile,
                        licenseNo: currentLicense.licenseNo,
                        coaId: coaId,
                    },
                });
            } else {
                acceptedFiles.forEach((file) => {
                    formData.append("files", file);
                });
                response = await apiClient.post(`/processCOA/`, formData, {
                    headers: {
                        company: currentLicense.company,
                        state: currentLicense.state,
                        industry: currentLicense.industry,
                        profile: currentLicense.profile,
                        licenseNo: currentLicense.licenseNo,
                    },
                });
            }

            const { jobId } = response.data || {};
            if (!jobId) {
                setCOAProcessing(false);
                toast.error("No jobId returned from server.");
                return;
            }

            setCOAProcessing(false);
            const pollUrl = coaId
                ? `/processCOA/uploadPanel/job/${jobId}`
                : `/processCOA/job/${jobId}`;
            submitJobs([{ jobId, pollUrl, fileName: acceptedFiles[0].name }]);
        } catch (error) {
            console.error(error);
            setCOAProcessing(false);
            if (error.response?.status === 400) {
                toast.error("Scanned or altered copies of COAs are not allowed. Please try again.");
            } else {
                toast.error(error.userMessage || error.message);
            }
        }
    };

    const onDrop = useCallback((acceptedFiles) => {
        processFiles(acceptedFiles);
    }, [currentLicense, coaId]);

    const combinedStyle = className ? undefined : (isHovered ? { ...baseStyle, ...hoverStyle } : baseStyle);
    const { getRootProps, getInputProps } = useDropzone({ onDrop });

    return (
        <div
            {...getRootProps()}
            className={className}
            style={combinedStyle}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            {className ? (
                <>
                    <span className="coa-option__icon">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    </span>
                    Upload COA
                </>
            ) : (
                <div style={{ color: "var(--color-text)" }}>Upload COA</div>
            )}
            <input {...getInputProps()} />
        </div>
    );
};

export default COAUpload;
